import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { CashflowForecastData, fetchCashflowForecast } from '../../helpers/plxOrdersData';
import Explainer from '../../components/Explainer';
import InfoTip from '../../components/InfoTip';
import { tokens } from '../../theme';
import { useChartTheme } from '../../themes/chartTheme';
import LoadingSpinner from '../../components/LoadingSpinner';
import { formatNumber } from '../../helpers/numberUtils';
import TableWidget, { HeaderMappingItem } from '../../helpers/tableBuilder';

const layerLabels: Record<string, string> = {
  existing: 'Bestehend',
  anfo: 'ANFO',
  no_lba: 'Ohne LBA',
};

const productTypeOptions = [
  { value: 'quartalsabo', label: 'Quartalsabo' },
  { value: 'upsell', label: 'Upsell' },
];

const CashflowForecastTab: FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const chartTheme = useChartTheme();
  const [data, setData] = useState<CashflowForecastData | null>(null);
  const [loading, setLoading] = useState(true);
  // Ladefehler sichtbar machen — vorher drehte der Spinner endlos (`!data`), Bedienprüfung 28.10.
  const [fehler, setFehler] = useState<string | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(['quartalsabo', 'upsell']);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      setLoading(true);
      setFehler(null);
      try {
        const result = await fetchCashflowForecast(selectedTypes);
        if (!cancelled) setData(result);
      } catch (error) {
        console.error('Error loading cashflow forecast:', error);
        if (!cancelled) setFehler(error instanceof Error ? error.message : String(error));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadData();
    return () => {
      cancelled = true;
    };
  }, [selectedTypes]);

  const handleTypeChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const next = typeof value === 'string' ? value.split(',') : value;
    if (next.length > 0) setSelectedTypes(next);
  };

  if (loading) {
    return <LoadingSpinner />;
  }
  if (fehler || !data) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Die Cashflow-Prognose konnte nicht geladen werden{fehler ? ` (${fehler})` : ''}. Bitte die Seite neu laden.
      </Alert>
    );
  }

  const { weeks, summary, details } = data;

  // Amount-weighted probability (bereinigt / brutto) so this KPI matches the
  // table summary — "von den erwarteten Euro, welcher Anteil kommt real rein".
  const avgProbability =
    summary.total_forecast_gross > 0
      ? summary.total_forecast_adjusted / summary.total_forecast_gross
      : 0;

  // Subscription breakdown for KPI subtitle
  const quartalsaboCount = summary.active_subscriptions - summary.active_upsells;

  const kpiCards = [
    {
      title: 'Gesamt-Prognose',
      value: formatNumber(summary.total_forecast_gross, 0) + ' \u20AC',
      subtitle: `Bereinigt: ${formatNumber(summary.total_forecast_adjusted, 0)} \u20AC`,
      color: colors.greenAccent[600],
      tooltip:
        'Wie viel Geld wir in den n\u00E4chsten 13 Wochen erwarten. Brutto = die volle f\u00E4llige Rechnung inkl. MwSt. (Quartalsabo: Quartalspreis, Upsell: Jahrespreis). Bereinigt = Brutto abz\u00FCglich erwarteter Ausf\u00E4lle \u2014 das ist die realistische Erwartung.',
    },
    {
      title: 'Aktive Abonnements',
      value: formatNumber(summary.active_subscriptions, 0),
      subtitle:
        selectedTypes.length === 2
          ? `${quartalsaboCount} Quartalsabo, ${summary.active_upsells} Upsell`
          : `${summary.with_lba} mit LBA, ${summary.without_lba} ohne`,
      color: colors.blueAccent[600],
      tooltip:
        'Zahlende Abos, deren n\u00E4chste Abrechnung in die n\u00E4chsten 13 Wochen f\u00E4llt (bei Quartalsabos praktisch alle aktiven; bei Upsells nur die mit j\u00E4hrlicher Verl\u00E4ngerung im Fenster). Nicht enthalten: Testphasen und Einmalk\u00E4ufe.',
    },
    {
      title: 'ANFOs in Testphase',
      value: formatNumber(summary.active_trials, 0),
      subtitle:
        summary.active_trials > 0
          ? summary.upsell_trials > 0
            ? `${formatNumber(
                details.filter((d) => d.layer === 'anfo').reduce((s, d) => s + d.invoice_amount, 0),
                0,
              )} \u20AC brutto (${summary.upsell_trials} Upsell)`
            : `${formatNumber(
                details.filter((d) => d.layer === 'anfo').reduce((s, d) => s + d.invoice_amount, 0),
                0,
              )} \u20AC brutto`
          : 'Keine aktiven Trials',
      color: '#ff9800',
      tooltip:
        'Neue Bestellungen in der kostenlosen Testphase (noch keine Zahlung). Ihr Beitrag zum Forecast wird mit der historischen Conversion-Rate des Produkts gewichtet \u2014 also nur der Anteil, der erfahrungsgem\u00E4\u00DF zahlender Kunde wird.',
    },
    {
      title: '\u00D8 Zahlungswahrscheinlichkeit',
      value: formatNumber(avgProbability * 100, 1) + ' %',
      subtitle: `${summary.dunning_distribution.find((d) => d.level === 0)?.count || 0} ohne Mahnung`,
      color: colors.primary[600],
      tooltip:
        'Von den erwarteten Euro (Brutto), welcher Anteil kommt erfahrungsgem\u00E4\u00DF real rein (= Bereinigt \u00F7 Brutto). Bestehende Abos gewichtet nach Mahnstufe, Testphasen nach Conversion-Rate.',
    },
  ];

  // Chart data (brutto)
  const chartData = weeks.map((w) => ({
    week: w.week_label,
    'Bestehende Abos': w.existing_gross,
    'ANFOs erwartet': w.anfo_gross,
    'Ohne LBA gesch\u00E4tzt': w.no_lba_gross,
  }));

  // Product breakdown table
  const productHeaderMapping: Record<string, HeaderMappingItem> = {
    product: { name: 'Produkt', type: 'string' },
    count: { name: 'Anzahl', type: 'number', digits: 0 },
    gross: { name: 'Brutto', type: 'number', digits: 2, attach: ' \u20AC' },
    adjusted: {
      name: 'Bereinigt',
      type: 'number',
      digits: 2,
      attach: ' \u20AC',
    },
    share: { name: 'Anteil', type: 'number', digits: 1, attach: ' %' },
  };

  const productTableData = summary.product_breakdown.map((p) => ({
    ...p,
    share: summary.total_forecast_gross > 0 ? (p.gross / summary.total_forecast_gross) * 100 : 0,
  }));

  const computeProductSummary = (tableData: (typeof productTableData)[number][]) => {
    if (!tableData.length) {
      return {
        product: 'Insgesamt',
        count: 0,
        gross: 0,
        adjusted: 0,
        share: 0,
      };
    }
    const s = {
      product: 'Insgesamt',
      count: 0,
      gross: 0,
      adjusted: 0,
      share: 100,
    };
    tableData.forEach((item) => {
      s.count += item.count || 0;
      s.gross += Number(item.gross) || 0;
      s.adjusted += Number(item.adjusted) || 0;
    });
    return s;
  };

  // Detail table
  const detailHeaderMapping: Record<string, HeaderMappingItem> = {
    ordernumber: { name: 'Bestellnr.', type: 'string' },
    product_name: { name: 'Produkt', type: 'string' },
    product_type_label: { name: 'Produkttyp', type: 'string' },
    invoice_amount: {
      name: 'Rechnungsbetrag',
      type: 'number',
      digits: 2,
      attach: ' \u20AC',
    },
    mahnstufe: { name: 'Mahnstufe', type: 'string' },
    lba_week: { name: 'LBA Woche', type: 'string' },
    expected_payment_date: { name: 'Erw. Zahlung', type: 'string' },
    probability: {
      name: 'Wahrsch.',
      type: 'number',
      digits: 0,
      attach: ' %',
    },
    adjusted_amount: {
      name: 'Bereinigt',
      type: 'number',
      digits: 2,
      attach: ' \u20AC',
    },
    layer_label: { name: 'Typ', type: 'string' },
  };

  const detailTableData = details.map((d) => ({
    ...d,
    probability: d.probability * 100,
    product_type_label: d.product_type === 'upsell' ? 'Upsell' : 'Quartalsabo',
    layer_label: layerLabels[d.layer] || d.layer,
    mahnstufe: d.mahnstufe ?? '-',
    lba_week: d.lba_week ?? '-',
    expected_payment_date: d.expected_payment_date
      ? new Date(d.expected_payment_date).toLocaleDateString('de-DE')
      : '-',
  }));

  const computeDetailSummary = (tableData: (typeof detailTableData)[number][]) => {
    if (!tableData.length) {
      return {
        ordernumber: 'Insgesamt',
        product_name: '',
        product_type_label: '',
        invoice_amount: 0,
        mahnstufe: '',
        lba_week: '',
        expected_payment_date: '',
        probability: 0,
        adjusted_amount: 0,
        layer_label: '',
        layer: 'existing' as const,
        artikel: '',
        product_type: 'quartalsabo' as const,
      };
    }
    const s = {
      ordernumber: 'Insgesamt',
      product_name: '',
      product_type_label: '',
      artikel: '',
      product_type: 'quartalsabo' as const,
      layer: 'existing' as const,
      invoice_amount: 0,
      mahnstufe: '' as any,
      lba_week: '',
      expected_payment_date: '',
      probability: 0,
      adjusted_amount: 0,
      layer_label: `${tableData.length} Bestellungen`,
    };
    tableData.forEach((item) => {
      s.invoice_amount += Number(item.invoice_amount) || 0;
      s.adjusted_amount += Number(item.adjusted_amount) || 0;
    });
    s.probability = s.invoice_amount > 0 ? (s.adjusted_amount / s.invoice_amount) * 100 : 0;
    return s;
  };

  return (
    <Box>
      <Explainer
        title="Was zeigt der Forecast — und wie werden die Zahlen berechnet?"
        sections={[
          {
            label: 'Datengrundlage',
            body: 'Alle zahlenden FIT-Abos (Quartalsabo + Upsell) und laufende Testphasen. Für jedes Abo ist bekannt, bis zu welcher Kalenderwoche es bezahlt ist — daraus ergibt sich die nächste fällige Zahlung.',
          },
          {
            label: 'Berechnung',
            body: 'Jede erwartete Zahlung wird auf ihre Kalenderwoche gelegt. Brutto = die volle fällige Rechnung inkl. MwSt. (Quartalsabo: Quartalspreis, Upsell: Jahrespreis). Bereinigt = Brutto abzüglich erwarteter Ausfälle (bestehende Abos je nach Mahnstufe, Testphasen je nach Conversion-Rate).',
          },
          {
            label: 'Aussage',
            body: 'Wie viel Geld in den nächsten 13 Wochen eingehen sollte. Die bereinigte Zahl ist die realistische Erwartung, die Brutto-Zahl das theoretische Maximum.',
          },
          {
            label: 'Wichtig',
            body: 'Enthält Quartalsabos (vierteljährliche Abrechnung) und Upsells (Jahresabos, einmal jährlich abgerechnet) — keine Einmalkäufe. Zahlungsart (Lastschrift/Rechnung) und überfällige offene Posten sind nicht enthalten. Der Forecast ist „live" und rechnet sich bei jedem Aufruf neu.',
          },
        ]}
      />

      {/* Product Type Filter */}
      <Box sx={{ mb: 2 }}>
        <FormControl sx={{ minWidth: 280 }} size="small">
          <InputLabel>Produkttyp</InputLabel>
          <Select<string[]>
            multiple
            value={selectedTypes}
            onChange={handleTypeChange}
            input={<OutlinedInput label="Produkttyp" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={productTypeOptions.find((o) => o.value === value)?.label || value}
                    size="small"
                  />
                ))}
              </Box>
            )}
          >
            {productTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {summary.without_lba > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {summary.without_lba} Abonnements ohne Abrechnungsdaten — Prognose basiert auf geschätzten
          Zahlungsintervallen.
        </Alert>
      )}

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpiCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Tooltip title={card.tooltip} arrow placement="top">
              <Card elevation={2} sx={{ height: '100%', cursor: 'help' }}>
                <CardContent>
                  <Typography variant="subtitle2" color={colors.grey[100]} gutterBottom>
                    {card.title}
                  </Typography>
                  <Typography variant="h4" sx={{ color: card.color, fontWeight: 'bold', mb: 0.5 }}>
                    {card.value}
                  </Typography>
                  <Typography variant="caption" color={colors.grey[300]}>
                    {card.subtitle}
                  </Typography>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
        ))}
      </Grid>

      {/* Stacked Bar Chart */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" mb={2}>
            Wöchentliche Zahlungseingänge (brutto)
            <InfoTip text="Jeder Balken = erwarteter Brutto-Zahlungseingang einer Kalenderwoche, aufgeteilt nach bestehenden Abos, laufenden Testphasen und Abos ohne Abrechnungsdaten. Die Woche ergibt sich aus der n\u00E4chsten f\u00E4lligen Abrechnung des Abos." />
          </Typography>
          <Box height="400px">
            <ResponsiveBar
              data={chartData}
              keys={['Bestehende Abos', 'ANFOs erwartet', 'Ohne LBA gesch\u00E4tzt']}
              indexBy="week"
              theme={chartTheme}
              margin={{ top: 20, right: 160, bottom: 50, left: 80 }}
              padding={0.3}
              valueScale={{ type: 'linear' }}
              indexScale={{ type: 'band', round: true }}
              colors={['#4caf50', '#ff9800', '#9e9e9e']}
              borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: -30,
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'EUR',
                legendPosition: 'middle',
                legendOffset: -60,
                format: (value) =>
                  Number(value) >= 1000 ? `${(Number(value) / 1000).toFixed(0)}k` : String(value),
              }}
              labelSkipWidth={12}
              labelSkipHeight={12}
              labelTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
              legends={[
                {
                  dataFrom: 'keys',
                  anchor: 'bottom-right',
                  direction: 'column',
                  justify: false,
                  translateX: 150,
                  translateY: 0,
                  itemsSpacing: 2,
                  itemWidth: 140,
                  itemHeight: 20,
                  itemDirection: 'left-to-right',
                  itemOpacity: 0.85,
                  symbolSize: 12,
                  symbolShape: 'square',
                },
              ]}
              tooltip={({ id, value, indexValue }) => (
                <Box
                  sx={{
                    padding: '8px 12px',
                    color: '#222',
                    background: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  <strong>{indexValue}</strong>
                  <br />
                  {id}: {formatNumber(Number(value), 2)} €
                </Box>
              )}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Dunning Distribution */}
      {summary.dunning_distribution.length > 0 && (
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            Mahnstufen:
            <InfoTip text="Wie viele Abos in welcher Mahnstufe stehen. Stufe 0 = keine Mahnung (Zahlung fast sicher), höhere Stufen = offene Mahnungen (Zahlung unsicherer). Bestimmt die Zahlungswahrscheinlichkeit im Forecast." />
          </Typography>
          {summary.dunning_distribution.map((d) => (
            <Tooltip
              key={d.level}
              title={`Mahnstufe ${d.level}: Zahlungswahrscheinlichkeit ${
                { 0: '95%', 1: '65%', 2: '35%', 3: '15%', 4: '0%' }[d.level] || 'unbekannt'
              }`}
              arrow
              placement="top"
            >
              <Chip
                label={`Stufe ${d.level}: ${d.count}x (${formatNumber(d.total_amount, 0)} \u20AC)`}
                color={d.level === 0 ? 'success' : d.level <= 2 ? 'warning' : 'error'}
                variant="outlined"
                sx={{ cursor: 'help' }}
              />
            </Tooltip>
          ))}
        </Box>
      )}

      {/* Product Breakdown Table */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h5" mb={2}>
            Produkt-Aufschlüsselung
            <InfoTip text="Erwarteter Zahlungseingang der n\u00E4chsten 13 Wochen je Produkt. Brutto = volle f\u00E4llige Rechnung inkl. MwSt. (Upsells werden j\u00E4hrlich abgerechnet), Bereinigt = abz\u00FCglich erwarteter Ausf\u00E4lle. Anteil = Anteil des Produkts am bereinigten Gesamt-Forecast." />
          </Typography>
          <Box
            sx={{
              backgroundColor: colors.primary[400],
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <TableWidget
              key={`products-${selectedTypes.join(',')}`}
              getData={async () => productTableData}
              computeSummary={computeProductSummary}
              detailsHeaderMapping={productHeaderMapping}
              initialSortColumn="gross"
              sortOrder="desc"
              excelDownloadName="Cashflow_Prognose_Produkte"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Detail Table */}
      <Card elevation={2}>
        <CardContent>
          <Typography variant="h5" mb={2}>
            Einzelne Bestellungen
            <InfoTip text="Jede Zeile ist ein Abo mit seiner nächsten erwarteten Zahlung. Rechnungsbetrag = volle fällige Rechnung brutto (Quartalsabo: Quartalspreis, Upsell: Jahrespreis). LBA-Woche = bis zu welcher Kalenderwoche das Abo bereits bezahlt ist. Wahrsch. = wie sicher die Zahlung erwartet wird (Mahnstufe bzw. Conversion-Rate)." />
          </Typography>
          <Box
            sx={{
              backgroundColor: colors.primary[400],
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <TableWidget
              key={`details-${selectedTypes.join(',')}`}
              getData={async () => detailTableData}
              computeSummary={computeDetailSummary}
              detailsHeaderMapping={detailHeaderMapping}
              initialSortColumn="invoice_amount"
              sortOrder="desc"
              excelDownloadName="Cashflow_Prognose_Details"
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CashflowForecastTab;
